import { MutableRefObject, useEffect } from 'react';
import usePrevious from '../usePrevious';

// Collects the dotted field paths of every leaf error, in order, so we can scroll to the first
// one. Handles nested objects and arrays (e.g. Formik field arrays like `contents.0.unitPrice`).
const collectErrorPaths = (errors: any, prefix = ''): string[] => {
	if (errors == null) return [];

	// A leaf error is a string message.
	if (typeof errors === 'string') return prefix ? [prefix] : [];

	if (Array.isArray(errors)) {
		return errors.flatMap((item, index) =>
			collectErrorPaths(item, prefix ? `${prefix}.${index}` : `${index}`)
		);
	}

	if (typeof errors === 'object') {
		return Object.keys(errors).flatMap(key =>
			collectErrorPaths(errors[key], prefix ? `${prefix}.${key}` : key)
		);
	}

	return [];
};

const useScrollToFirstFormikError = (
	errors: any,
	isSubmitting: boolean,
	isValid: boolean,
	formRef: MutableRefObject<any>
) => {
	const prevIsSubmitting = usePrevious(isSubmitting);

	useEffect(() => {
		if (prevIsSubmitting && !isSubmitting && !isValid) {
			const form = formRef.current;
			if (!form) return;

			const errorPaths = collectErrorPaths(errors);

			// Find the first errored field that actually has a rendered input we can scroll to.
			// A field name may resolve to a `name=` or an `id=` (e.g. dropdowns use inputId).
			let elem: Element | null = null;
			for (const path of errorPaths) {
				elem =
					form.querySelector(`[name="${path}"]`) ||
					form.querySelector(`[id="${path}"]`) ||
					form.querySelector(`[id="${path}-input"]`);
				if (elem) break;
			}

			// Fall back to the first error message on the page (covers array-level errors such as
			// "at least one item" that don't map to a specific input).
			if (!elem) {
				elem = form.querySelector('[data-error-message]');
			}

			if (elem) {
				const y = elem.getBoundingClientRect().top + window.scrollY - 120;
				window.scroll({
					top: y,
					behavior: 'smooth',
				});
			}
		}
	}, [errors, isSubmitting, isValid, prevIsSubmitting, formRef]);
};

export default useScrollToFirstFormikError;
